"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { toast } from "sonner";
import svgPaths from "@/assets/svg/svg-zhqsc4us7g";
import imgHeroImageRight from "@/assets/images/landing/b7cf068f2cd000acb93c2d6dcf2c5d20fcc2d70c.png";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";
import { scrollIntoViewWithOffset } from "@/lib/smooth-scroll";

export default function MobileFAQContent() {
	const [openAccordion, setOpenAccordion] = useState<number | null>(0);
	const [isUpgrading, setIsUpgrading] = useState(false);

	const toggleAccordion = (index: number) => {
		setOpenAccordion((prev) => (prev === index ? null : index));
	};

	// When an answer opens, scroll it into view below the header for visibility and accessibility
	useEffect(() => {
		if (openAccordion === null) return;
		const timer = setTimeout(() => {
			const el = document.querySelector(
				`[data-faq-answer="${openAccordion}"]`
			);
			scrollIntoViewWithOffset(el ?? null, 100);
		}, 150);
		return () => clearTimeout(timer);
	}, [openAccordion]);

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	const questionBaseClasses =
		"w-full backdrop-blur-md bg-[#1a2c20] border border-[rgba(255,255,255,0.2)] px-4 py-3 flex items-center justify-between gap-4";

	const getQuestionClasses = (index: number) => {
		const isOpen = openAccordion === index;
		// When open: only round the top corners; when closed: fully rounded
		return `${questionBaseClasses} ${
			isOpen ? "rounded-t-[12px]" : "rounded-[12px]"
		}`;
	};

	return (
		<div className="bg-[#16251c] min-h-screen w-full overflow-x-hidden">
			{/* Container with max width for mobile */}
			<div className="mx-auto" style={{ width: "100%" }}>
				{/* Hero Section with Background Images */}
				<div
					className="relative h-[540px] bg-[#1a2c20] overflow-hidden mb-8"
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					{/* Background building images */}
					<Image
						src={imgHeroImageRight}
						alt=""
						className="absolute left-[128px] top-[340px] w-[365px] h-[227px] object-cover max-w-none pointer-events-none opacity-50"
					/>

					{/* Hero Content */}
					<div
						className="relative z-10 px-6 pt-8"
						style={{ width: "90%" }}
					>
						<h1
							className="text-white text-2xl font-extrabold leading-7 tracking-[0.5px] mb-3"
							style={{ paddingTop: "8rem" }}
						>
							Frequently Asked Questions
						</h1>
						<p className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed">
							At Tax Lien Vest, we provide serious investors with
							professional-grade property intelligence, turning
							complex data into actionable investment
							opportunities. Below, we answer the most common
							questions about our platform, features, and
							benefits.
						</p>
					</div>
				</div>

				<div
					style={{
						width: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					<div
						style={{
							width: "90%",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						{/* Platform Access & Features Section */}
						<div className="mx-4 mb-6">
							<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
								Platform Access & Features
							</h2>

							{/* Accordion 1 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(0)}
									className={getQuestionClasses(0)}
								>
									<span className="text-white text-sm leading-5 text-left">
										What property data does Tax Lien Vest
										provide for investment research?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 0
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 0 && (
									<div
										data-faq-answer="0"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p className="mb-3">
												Tax Lien Vest delivers complete
												property intelligence across
												five key categories:
											</p>
											<ul className="list-disc mb-3 ml-5">
												<li className="mb-2">
													<span>
														Geographic Data:
														Latitude/longitude,
														parcel IDs, census tract
														info, zoning details.
													</span>
												</li>
												<li className="mb-2">
													<span>
														Owner Contact Data:
														Original:Full mailing
														addresses (city, state,
														ZIP) for direct
														negotiation.
													</span>
												</li>
												<li className="mb-2">
													<span>
														Property
														Characteristics: Square
														footage, number of
														rooms, stories, year
														built, zoning codes, and
														detailed use codes.
													</span>
												</li>
												<li className="mb-2">
													<span>
														Financial Data: Total
														debt amounts, appraised
														values (with valuation
														type), and historical
														sale prices.
													</span>
												</li>
												<li className="mb-2">
													<span>
														Legal Details: Court
														case numbers, attorney
														contacts, sheriff sale
														dates, and defendant
														details.
													</span>
												</li>
												<li className="mb-2">
													<span>
														Owner Contact Data: Full
														mailing addresses (city,
														state, ZIP) for direct
														negotiation.
													</span>
												</li>
											</ul>
											<p>
												Start your free trial today and
												unlock actionable investment
												insights
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 2 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(1)}
									className={getQuestionClasses(1)}
								>
									<span className="text-white text-sm leading-5 text-left">
										What&apos;s the difference between
										Preview and Premium access?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 1
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 1 && (
									<div
										data-faq-answer="1"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												Preview access allows you to
												browse basic property
												information, while Premium
												access unlocks full property
												intelligence including legal
												details, financial data, and
												contact information for serious
												investment decisions.
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 3 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(2)}
									className={getQuestionClasses(2)}
								>
									<span className="text-white text-sm leading-5 text-left">
										How does Tax Lien Vest ensure data
										accuracy and reliability?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 2
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 2 && (
									<div
										data-faq-answer="2"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												All our data is sourced from
												official court records, county
												databases, and verified legal
												documents. We continuously
												update our database to ensure
												you have access to the most
												current and accurate property
												information available.
											</p>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Property Research & Data Intelligence Section */}
						<div className="mx-4 mb-6">
							<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
								Property Research & Data Intelligence
							</h2>

							{/* Accordion 4 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(3)}
									className={getQuestionClasses(3)}
								>
									<span className="text-white text-sm leading-5 text-left">
										How does Tax Lien Vest ensure data
										accuracy and reliability?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 3
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 3 && (
									<div
										data-faq-answer="3"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												We verify all data against
												official legal and court records
												to ensure maximum accuracy and
												reliability for your investment
												decisions.
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 5 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(4)}
									className={getQuestionClasses(4)}
								>
									<span className="text-white text-sm leading-5 text-left">
										Can I track foreclosure timelines?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 4
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 4 && (
									<div
										data-faq-answer="4"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												Yes, our platform provides
												detailed foreclosure timeline
												tracking including sheriff sale
												dates, court case progression,
												and important deadline
												notifications to help you stay
												on top of investment
												opportunities.
											</p>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Geographic & Search Features Section */}
						<div className="mx-4 mb-6">
							<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
								Geographic & Search Features
							</h2>

							{/* Accordion 6 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(5)}
									className={getQuestionClasses(5)}
								>
									<span className="text-white text-sm leading-5 text-left">
										How can I search for properties in
										specific areas?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 5
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 5 && (
									<div
										data-faq-answer="5"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												You can search by state, county,
												city, or ZIP code. Our advanced
												filters also allow you to narrow
												results by property type, debt
												amount, auction dates, and more
												to find exactly what you&apos;re
												looking for.
											</p>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Trial Access & Support Section */}
						<div className="mx-4 mb-6">
							<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
								Trial Access & Support
							</h2>

							{/* Accordion 7 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(6)}
									className={getQuestionClasses(6)}
								>
									<span className="text-white text-sm leading-5 text-left">
										How does the free trial work?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 6
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 6 && (
									<div
										data-faq-answer="6"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												Start your 14-day free trial
												with no credit card required.
												You&apos;ll have full access to
												all Premium features, and you
												can cancel anytime with no
												obligations.
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 8 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(7)}
									className={getQuestionClasses(7)}
								>
									<span className="text-white text-sm leading-5 text-left">
										Which states are currently covered by
										Tax Lien Vest?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 7
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 7 && (
									<div
										data-faq-answer="7"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												We currently cover multiple
												states with comprehensive
												property lien data. Our coverage
												is continuously expanding. Check
												our coverage map for the most
												up-to-date information on
												available states and counties.
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 9 */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(8)}
									className={getQuestionClasses(8)}
								>
									<span className="text-white text-sm leading-5 text-left">
										What support is available?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 8
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 8 && (
									<div
										data-faq-answer="8"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												Premium members receive priority
												support via email, chat, and
												phone. Our team of property
												investment specialists is here
												to help you make the most of the
												platform and answer any
												questions about your research.
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 9 - Cancel Subscription */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(9)}
									className={getQuestionClasses(9)}
								>
									<span className="text-white text-sm leading-5 text-left">
										How do I cancel my subscription?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 9
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 9 && (
									<div
										data-faq-answer="9"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												Go to Manage Profile and easily
												cancel your membership. You can
												cancel anytime.
											</p>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Export & Integration Features Section */}
						<div className="mx-4 mb-6">
							<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
								Export & Integration Features
							</h2>

							{/* Accordion 10 - Google My Maps */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(10)}
									className={getQuestionClasses(10)}
								>
									<span className="text-white text-sm leading-5 text-left">
										Can I export properties to Google My
										Maps?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 10
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 10 && (
									<div
										data-faq-answer="10"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												Yes! Premium users can export
												property data directly to Google
												My Maps. This feature allows you
												to visualize properties on a
												map, share locations with your
												team, and create custom maps for
												your investment research. Simply
												select the properties you want
												to export and use the export
												feature to generate a Google My
												Maps compatible file.
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 11 - PropStream */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(11)}
									className={getQuestionClasses(11)}
								>
									<span className="text-white text-sm leading-5 text-left">
										Can I export data to PropStream?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 11
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 11 && (
									<div
										data-faq-answer="11"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												Yes, Tax Lien Vest supports
												exporting property data in
												formats compatible with
												PropStream and other real estate
												research platforms. You can
												export your selected properties
												as CSV or Excel files, which can
												then be imported into PropStream
												for further analysis and
												workflow integration.
											</p>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Notifications Section */}
						<div className="mx-4 mb-6">
							<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
								Notifications
							</h2>

							{/* Accordion 12 - Notification Types */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(12)}
									className={getQuestionClasses(12)}
								>
									<span className="text-white text-sm leading-5 text-left">
										What types of notifications are
										available?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 12
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 12 && (
									<div
										data-faq-answer="12"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p className="mb-2">
												Tax Lien Vest offers several
												types of notifications to keep
												you informed:
											</p>
											<ul className="list-disc ms-4 space-y-1">
												<li>
													New property matches based
													on your saved search
													criteria
												</li>
												<li>
													Upcoming auction dates and
													deadlines
												</li>
												<li>Property status changes</li>
												<li>
													Price updates and valuation
													changes
												</li>
												<li>
													Account and subscription
													updates
												</li>
											</ul>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 13 - Setup Notifications */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(13)}
									className={getQuestionClasses(13)}
								>
									<span className="text-white text-sm leading-5 text-left">
										How do I set up notifications?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 13
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 13 && (
									<div
										data-faq-answer="13"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p className="mb-2">
												To set up notifications:
											</p>
											<ol className="list-decimal ms-4 space-y-1 mb-2">
												<li>
													Go to your Profile or
													Settings page
												</li>
												<li>
													Navigate to the
													Notifications section
												</li>
												<li>
													Select the types of
													notifications you want to
													receive
												</li>
												<li>
													Choose your preferred
													delivery method (email,
													in-app, or both)
												</li>
												<li>
													Set your notification
													frequency preferences
												</li>
												<li>Save your preferences</li>
											</ol>
											<p>
												You can customize notifications
												for specific properties, search
												criteria, or auction dates.
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Accordion 14 - Cancel Notifications */}
							<div className="mb-4">
								<button
									type="button"
									onClick={() => toggleAccordion(14)}
									className={getQuestionClasses(14)}
								>
									<span className="text-white text-sm leading-5 text-left">
										How do I cancel or manage my
										notifications?
									</span>
									<svg
										className={`w-5 h-5 shrink-0 transition-transform ${
											openAccordion === 14
												? "rotate-180"
												: ""
										}`}
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d="M6 9L12 15L18 9"
											stroke="white"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</button>
								{openAccordion === 14 && (
									<div
										data-faq-answer="14"
										className="backdrop-blur-md bg-[#1a2c20] border-x border-b border-[rgba(255,255,255,0.2)] rounded-b-[12px] px-4 py-4 -mt-[1px]"
									>
										<div className="text-[#8c99a7] text-xs leading-[20px]">
											<p>
												You can easily manage or cancel
												notifications at any time. Go to
												your Profile or Settings page,
												navigate to the Notifications
												section, and toggle off any
												notification types you no longer
												want to receive. You can also
												unsubscribe from specific
												property alerts or modify your
												notification preferences.
												Changes take effect immediately.
											</p>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Need Help Card */}
						<div className="mx-4 mb-8">
							<div className="backdrop-blur-md bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[21px] p-6 flex flex-col gap-5">
								<div
									className="backdrop-blur-md rounded-[85px] w-10 h-10 flex items-center justify-center"
									style={{
										backgroundImage:
											"linear-gradient(90deg, rgba(101, 222, 158, 0.12) 0%, rgba(101, 222, 158, 0.12) 100%)",
									}}
								>
									<svg
										className="w-6 h-6"
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											d={svgPaths.p5ce5900}
											stroke="#A7FF5E"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										/>
									</svg>
								</div>
								<div className="flex flex-col gap-2">
									<h3 className="text-white text-base font-bold leading-6">
										Need Help?
									</h3>
									<p className="text-[#8c99a7] text-sm leading-6">
										For any queries reach out to us at{" "}
										<span className="text-[#a7ff5e]">
											support@TaxLienVest.com
										</span>
									</p>
								</div>
							</div>
						</div>

						{/* CTA Section - Ready to Transform */}
						<div className="backdrop-blur-md px-6 py-8 mb-0">
							<div className="flex flex-col gap-7">
								<h2 className="text-white text-xl font-extrabold leading-7 text-center tracking-[0.5px]">
									Ready to Transform Your Tax Lien Research?
								</h2>
								<p className="text-xs leading-5 text-center tracking-[0.12px]">
									<span className="text-[#a7ff5e] font-semibold">
										Start your free trial
									</span>
									<span className="text-[rgba(255,255,255,0.7)]">
										{" "}
										and see why professional investors trust
										Tax Lien Vest for faster, smarter, and
										more profitable lien investing.
									</span>
								</p>

								<div className="flex flex-col gap-5">
									{/* Search Input */}
									<div className="bg-white rounded-[68px] px-4 py-2 flex items-center gap-1 border border-[#dde3e9] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] h-[56px]">
										<input
											type="text"
											placeholder="Search by State, County, City, and Zip code"
											className="flex-1 text-sm font-medium text-[#697b8e] bg-transparent outline-none leading-5 tracking-[-0.1504px]"
										/>
										<div className="bg-[#a7ff5e] w-10 h-10 rounded-[44px] flex items-center justify-center shrink-0 border border-[#12301b]">
											<svg
												className="w-[14px] h-[14px]"
												fill="none"
												viewBox="0 0 17 17"
											>
												<path
													d={svgPaths.p170ab80}
													stroke="#12301B"
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth="2.5"
												/>
											</svg>
										</div>
									</div>

									{/* CTA Text */}
									<button
										onClick={handleUpgrade}
										disabled={isUpgrading}
										className="text-xs leading-5 text-center tracking-[0.12px] bg-transparent border-none p-0 cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed w-full"
										type="button"
									>
										<span className="text-[#cbf7a6] font-semibold">
											Try Free for 14 days
										</span>
										<span className="text-[#dde3e9]">
											{" "}
											• No Credit Card Required • Cancel
											Anytime • Priority Support
										</span>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="bg-[#1a2c20] px-8 pt-6 pb-8">
					<div className="flex flex-col gap-8">
						{/* Footer Links */}
						<div className="flex items-center justify-center gap-[42px] text-white text-xs font-medium leading-5">
							<Link href="/about" className="whitespace-nowrap">
								About
							</Link>
							<Link
								href="/privacy-policy"
								className="whitespace-nowrap"
							>
								Privacy Policy
							</Link>
							<Link
								href="/terms-of-service"
								className="whitespace-nowrap"
							>
								Terms & Conditions
							</Link>
							<Link
								href="/contact-us"
								className="whitespace-nowrap"
							>
								Contact
							</Link>
						</div>

						{/* Social and Copyright Section */}
						<div className="flex flex-col gap-8 pt-8 border-t border-[#417714]">
							{/* Copyright */}
							<p className="text-[#dde3e9] text-sm text-center leading-5">
								© 2024 Tax Lien Vest. All rights reserved.
							</p>

							{/* Terms */}
							<p className="text-[#dde3e9] text-sm text-center leading-5">
								<span className="underline decoration-solid">
									Terms and Conditions
								</span>{" "}
								and{" "}
								<span className="underline decoration-solid">
									Privacy policy
								</span>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
